import clsx from 'clsx';
import { ButtonHTMLAttributes } from 'react';
import { Ico } from '../icons/ico';

const baseStyle = clsx(
  'flex flex-row justify-center items-center disabled:cursor-default!',
  'focus-visible:outline-2 focus-visible:outline-focus',
);

const largeStyle = clsx('px-5 py-4 gap-2 text-button rounded-md max-h-11');
const mediumStyle = clsx('px-3 py-2 gap-1.5 text-button rounded-md max-h-8');
const smallStyle = clsx('p-2 gap-1.5 text-button-small rounded max-h-7');

const largeIconOnly = clsx('text-button p-4 rounded-md');
const mediumIconOnly = clsx('text-button p-2.5 rounded-md');

const tertiaryLarge = clsx('p-1 gap-2 text-button rounded');
const tertiarySmall = clsx('p-1 gap-1.5 text-button-small rounded');

const interactiveLarge = clsx('p-2');
const interactiveMedium = clsx('p-2');
const interactiveSmall = clsx('p-2.5');

const shadow = clsx(
  'enabled:not-hover:not-active:not-focus-visible:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),0px_2px_4px_0px_rgba(0,0,0,0.06)]',
  'enabled:hover:not-active:not-focus-visible:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06),0px_4px_8px_0px_rgba(0,0,0,0.08)]',
);

const primaryStyle = clsx(
  'bg-blue-600 text-white',
  'enabled:hover:bg-blue-700',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'active:bg-blue-800',
  shadow,
);
const secondaryStyle = clsx(
  'bg-blue-50 text-blue-700',
  'enabled:hover:bg-blue-100 enabled:hover:text-blue-800',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'focus-visible:bg-blue-100 focus-visible:text-blue-800',
  'active:bg-blue-200 active:text-blue-900',
  shadow,
);
const tertiaryStyle = clsx(
  'bg-white text-gray-700',
  'enabled:hover:text-blue-700 enabled:hover:underline',
  'disabled:text-gray-400',
  'focus-visible:text-blue-700',
  'active:text-blue-800 active:underline active:decoration-blue-900',
);
const quaternaryStyle = clsx(
  'bg-gray-200 text-gray-900',
  'enabled:hover:bg-gray-200 enabled:hover:text-gray-700',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'focus-visible:bg-gray-100 focus-visible:text-gray-500 focus-visible:ring-2 focus-visible:ring-blue-400',
);
const destructivePrimaryStyle = clsx(
  'bg-red-500 text-white',
  'enabled:hover:bg-red-700',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'active:bg-red-800',
  shadow,
);
const destructiveSecondaryStyle = clsx(
  'bg-red-50 text-red-700',
  'enabled:hover:bg-red-100 enabled:hover:text-red-800',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'active:bg-gray-200 active:text-red-800',
  shadow,
);
const destructiveTertiaryStyle = clsx(
  'bg-white text-red-600',
  'enabled:hover:text-red-700 enabled:hover:underline',
  'disabled:text-gray-400 disabled:no-underline',
  'active:text-red-800 active:underline',
);
const interactiveStyle = clsx(
  'bg-none text-gray-600 rounded-md',
  'enabled:hover:bg-gray-200 enabled:hover:text-blue-700',
  'disabled:bg-gray-200 disabled:text-gray-400',
  'focus-visible:text-gray-600',
  'active:bg-gray-200 active:text-gray-900',
);

const normalIconSize: Record<ButtonSizes, string> = { L: '0.75rem', M: '0.75rem', S: '0.625rem' };
const interactiveIconSize: Record<ButtonSizes, string> = { L: '1.5rem', M: '1rem', S: '0.75rem' };

export type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'destructive-primary'
  | 'destructive-secondary'
  | 'destructive-tertiary'
  | 'interactive';

export type ButtonSizes = 'M' | 'L' | 'S';

export type IconPosition = 'start' | 'end';

export type ButtonProps = {
  icon?: Ico;
  variant?: ButtonVariants;
  size?: ButtonSizes;
  iconPosition?: IconPosition;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  icon: Icon,
  variant = 'primary',
  size = 'L',
  iconPosition = 'start',
  className,
  children,
  ...props
}: ButtonProps) {
  const iconSize = (variant === 'interactive' ? interactiveIconSize : normalIconSize)[size];
  const appliedIcon = Icon && <Icon width={iconSize} height={iconSize} />;
  return (
    <button
      className={clsx(
        baseStyle,
        variant === 'interactive'
          ? {
              S: interactiveSmall,
              M: interactiveMedium,
              L: interactiveLarge,
            }[size]
          : variant === 'tertiary' || variant === 'destructive-tertiary'
            ? {
                S: tertiarySmall,
                M: tertiaryLarge,
                L: tertiaryLarge,
              }[size]
            : Icon && !children
              ? {
                  S: smallStyle,
                  M: mediumIconOnly,
                  L: largeIconOnly,
                }[size]
              : {
                  S: smallStyle,
                  M: mediumStyle,
                  L: largeStyle,
                }[size],
        {
          primary: primaryStyle,
          secondary: secondaryStyle,
          tertiary: tertiaryStyle,
          quaternary: quaternaryStyle,
          'destructive-primary': destructivePrimaryStyle,
          'destructive-secondary': destructiveSecondaryStyle,
          'destructive-tertiary': destructiveTertiaryStyle,
          interactive: interactiveStyle,
        }[variant],
        className,
      )}
      {...props}
    >
      {iconPosition === 'start' && appliedIcon}
      {children}
      {iconPosition === 'end' && appliedIcon}
    </button>
  );
}
